import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import { Stack, Button, Dialog, TextField, Typography, DialogTitle, Autocomplete, DialogActions, DialogContent } from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { useGetOrganizationContactsQuery } from 'src/services/contacts/contacts.service';
import { useGetOrganizationCandidatesQuery } from 'src/services/candidates/candidates.service';
import { useAssignCommunicationActivityMutation } from 'src/services/integration/integrations.service';
import { ICommunicationActivity, CommunicationEntityType } from 'src/services/integration/integrations.types';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  activity: ICommunicationActivity | null;
  onClose: () => void;
  onSuccess?: () => void;
};

type AssignOption = { id: string; label: string; type: CommunicationEntityType; sublabel?: string };

/**
 * Assign an unmatched call/SMS to a candidate OR contact. Searches both record
 * types at once (grouped) and seeds the search with the activity's phone number
 * so likely matches surface immediately — since the user often can't recognise a
 * person from the number alone. The backend also writes a learned number→record
 * resolution and re-matches every other activity from that number.
 */
export default function AssignActivityModal({ open, activity, onClose, onSuccess }: Props) {
  const { t } = useTranslate();
  const authContext = useAuthContext();
  const orgId = authContext?.organization?.id;

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AssignOption | null>(null);

  // Seed the search with the activity's number when opened so matches appear
  // without the user having to know who the number belongs to.
  useEffect(() => {
    if (open) {
      setSearch(activity?.external_phone_number ?? '');
      setSelected(null);
    } else {
      setSearch('');
      setSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.id]);

  const enabled = open && search.trim().length >= 2;

  const { data: candidatesPage, isFetching: candidatesFetching } = useGetOrganizationCandidatesQuery(
    { search_query: search, page: { page: 1, per_page: 10 } } as any,
    { skip: !enabled }
  );
  const { data: contactsPage, isFetching: contactsFetching } = useGetOrganizationContactsQuery(
    { org_id: orgId, search_query: search, page: { page: 1, per_page: 10 } } as any,
    { skip: !enabled || !orgId }
  );

  const [assignActivity, { isLoading }] = useAssignCommunicationActivityMutation();

  const options: AssignOption[] = useMemo(() => {
    const candidateOptions: AssignOption[] = ((candidatesPage?.results ?? []) as any[]).map((c) => {
      const profile = c.candidate_profile ?? {};
      const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
      return {
        id: c.id,
        label: name || profile.email || c.id,
        type: CommunicationEntityType.CANDIDATE,
        sublabel: c.phone_number || profile.phone_number || profile.email || undefined,
      };
    });
    const contactOptions: AssignOption[] = ((contactsPage?.results ?? []) as any[]).map((c) => {
      const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
      return {
        id: c.id,
        label: name || c.email || c.id,
        type: CommunicationEntityType.CONTACT,
        sublabel: c.phone || c.email || undefined,
      };
    });
    return [...candidateOptions, ...contactOptions];
  }, [candidatesPage, contactsPage]);

  const isFetching = candidatesFetching || contactsFetching;

  const handleAssign = async () => {
    if (!activity || !selected) return;
    try {
      const result = await assignActivity({
        activityId: activity.id,
        entity_type: selected.type,
        entity_id: selected.id,
      }).unwrap();

      enqueueSnackbar(
        t('integrations.communications.assign.success', { count: result?.matched ?? 1 }),
        { variant: 'success' }
      );
      setSelected(null);
      setSearch('');
      onSuccess?.();
      onClose();
    } catch (error) {
      enqueueSnackbar(t('integrations.communications.assign.error'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('integrations.communications.assign.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {activity && (
            <Typography variant="body2" color="text.secondary">
              {t('integrations.communications.assign.subtitle', { number: activity.external_phone_number })}
            </Typography>
          )}

          <Autocomplete
            fullWidth
            options={options}
            loading={isFetching}
            value={selected}
            inputValue={search}
            onChange={(_e, value) => {
              setSelected(value);
              setSearch(value?.label ?? '');
            }}
            onInputChange={(_e, value, reason) => {
              if (reason === 'input' || reason === 'clear') setSearch(value);
            }}
            isOptionEqualToValue={(o, v) => o.id === v.id && o.type === v.type}
            getOptionLabel={(o) => o.label}
            groupBy={(o) => (o.type === CommunicationEntityType.CANDIDATE
              ? t('integrations.communications.assign.group_candidates')
              : t('integrations.communications.assign.group_contacts'))}
            renderOption={(props, o) => (
              <li {...props} key={`${o.type}:${o.id}`}>
                <Stack>
                  <Typography variant="body2">{o.label}</Typography>
                  {o.sublabel && (
                    <Typography variant="caption" color="text.secondary">{o.sublabel}</Typography>
                  )}
                </Stack>
              </li>
            )}
            noOptionsText={search.trim().length < 2
              ? t('integrations.communications.assign.search_hint')
              : t('integrations.communications.assign.no_results')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('integrations.communications.assign.record_label')}
                placeholder={t('integrations.communications.assign.record_placeholder')}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>{t('common.close')}</Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!selected} onClick={handleAssign}>
          {t('integrations.communications.assign.confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
